import React, { useEffect, useState } from 'react';
import { usePortalStore } from '../../store';
import { Shield, BookOpen, AlertTriangle, CheckCircle, BarChart3, Database, Plus, X, Gavel, Lock, Unlock, User } from 'lucide-react';

const ARCH_DOMAINS = ['db', 'api', 'agent', 'ui', 'storage', 'infra', 'process', 'code'] as const;

const MANDATE_ARCHETYPES = [
  'Enterprise Architect / CTO / COO',
  'Product Visionary / CPO',
  'Growth Hacker / CMO',
  'Financial Engineer / CFO',
  'Generalist Co-Founder',
] as const;

const KNOWN_SKILLS = [
  'architect', 'implement', 'verify', 'deploy',
  'specify', 'research', 'watchtower', 'canvas',
] as const;

const AddArchFactForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addArchFact } = usePortalStore();
  const [domain, setDomain] = useState<string>('api');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [confidence, setConfidence] = useState('inferred');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!key.trim() || !value.trim()) { setError('Key and value are required'); return; }
    setSaving(true);
    setError('');
    try {
      await addArchFact(domain, key.trim(), value.trim(), confidence);
      onClose();
    } catch (err: any) {
      setError(err.message ?? 'Failed to add fact');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-100">Add Architecture Fact</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Domain *</label>
            <select
              value={domain}
              onChange={e => setDomain(e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              {ARCH_DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Key *</label>
            <input
              type="text"
              value={key}
              onChange={e => setKey(e.target.value)}
              placeholder="e.g. primary_db_engine"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Value *</label>
            <textarea
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder="Fact description..."
              rows={3}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Confidence</label>
            <select
              value={confidence}
              onChange={e => setConfidence(e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="grounded">grounded — observed fact</option>
              <option value="inferred">inferred — deduction</option>
              <option value="speculative">speculative — assumption</option>
            </select>
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 rounded-lg text-sm text-white font-semibold transition-colors">
              {saving ? 'Adding...' : 'Add Fact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

type VerdictFilter = 'all' | 'queue_block' | 'block';

export const GovernanceLayer: React.FC = () => {
  const [expandedADR, setExpandedADR] = useState<string | null>(null);
  const [selectedRisk, setSelectedRisk] = useState<string | null>(null);
  const [showAddFact, setShowAddFact] = useState(false);
  const [filterDomain, setFilterDomain] = useState('');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>('all');
  const [blockSkillInput, setBlockSkillInput] = useState('');
  const [blockSkillReason, setBlockSkillReason] = useState('');
  const {
    principles,
    authorityMatrix,
    complianceStatuses,
    risks,
    adrHistory,
    archFacts,
    governorDecisions,
    boardDecisions,
    mandateArchetype,
    blockedSkills,
    loadPrinciples,
    loadAuthorityMatrix,
    loadComplianceStatuses,
    loadRisks,
    loadADRHistory,
    loadArchFacts,
    loadGovernorDecisions,
    loadBoardDecisions,
    loadMandateArchetype,
    setMandateArchetype,
    loadBlockedSkills,
    blockSkill,
    unblockSkill,
    resolveBoardDecision,
  } = usePortalStore();

  useEffect(() => {
    loadPrinciples();
    loadAuthorityMatrix();
    loadComplianceStatuses();
    loadRisks();
    loadADRHistory();
    loadArchFacts();
    loadGovernorDecisions();
    loadBoardDecisions();
    loadMandateArchetype();
    loadBlockedSkills();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-emerald-900 text-emerald-200';
      case 'in-progress':
        return 'bg-yellow-900 text-yellow-200';
      case 'non-compliant':
        return 'bg-red-900 text-red-200';
      case 'accepted':
        return 'bg-emerald-900 text-emerald-200';
      case 'proposed':
        return 'bg-yellow-900 text-yellow-200';
      case 'deprecated':
        return 'bg-red-900 text-red-200';
      default:
        return 'bg-slate-700 text-slate-300';
    }
  };

  const filteredFacts = filterDomain
    ? archFacts.filter(f => f.domain === filterDomain)
    : archFacts;

  const verdictBadge = (verdict: string) => {
    switch (verdict) {
      case 'EXECUTE': return 'bg-emerald-900 text-emerald-200';
      case 'QUEUE':   return 'bg-amber-900 text-amber-200';
      case 'BLOCK':   return 'bg-red-900 text-red-200';
      default:        return 'bg-slate-700 text-slate-300';
    }
  };

  const verdictBorder = (verdict: string) => {
    switch (verdict) {
      case 'EXECUTE': return 'border-l-emerald-500';
      case 'QUEUE':   return 'border-l-amber-500';
      case 'BLOCK':   return 'border-l-red-500';
      default:        return 'border-l-slate-600';
    }
  };

  const filteredDecisions = governorDecisions.filter(d => {
    if (verdictFilter === 'block') return d.routing_verdict === 'BLOCK';
    return true; // 'all' and 'queue_block' both show everything (near_miss only records QUEUE/BLOCK)
  });

  const reasonSnippet = (detail: string) => {
    try {
      const obj = JSON.parse(detail);
      const parts = Object.entries(obj)
        .filter(([, v]) => v !== null)
        .map(([k, v]) => `${k}=${v}`)
        .slice(0, 3);
      return parts.join(', ') || detail.slice(0, 80);
    } catch {
      return detail.slice(0, 80);
    }
  };

  return (
    <div className="space-y-6">
      {showAddFact && <AddArchFactForm onClose={() => { setShowAddFact(false); loadArchFacts(); }} />}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-100 mb-2">Governance Dashboard</h1>
        <p className="text-slate-400">Constitutional principles, authority matrix, compliance status, and risk registry</p>
      </div>

      {/* Mandate Archetype */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-amber-400" />
          Lucas Mandate Archetype
        </h2>
        <p className="text-xs text-slate-500 mb-3">Set the primary lens Lucas uses when making decisions and prioritising work.</p>
        <div className="flex items-center gap-3">
          <select
            value={mandateArchetype?.archetype ?? ''}
            onChange={e => setMandateArchetype(e.target.value)}
            className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="">— select archetype —</option>
            {MANDATE_ARCHETYPES.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          {mandateArchetype?.archetype && (
            <span className="text-xs text-slate-400">
              Active: <span className="text-amber-400 font-semibold">{mandateArchetype.archetype}</span>
            </span>
          )}
        </div>
      </div>

      {/* Skill Block/Unblock */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-red-400" />
          Skill Run Control
        </h2>
        <p className="text-xs text-slate-500 mb-4">Block a skill from being dispatched by the pipeline.</p>

        <div className="flex gap-2 mb-4">
          <select
            value={blockSkillInput}
            onChange={e => setBlockSkillInput(e.target.value)}
            className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">— select skill —</option>
            {KNOWN_SKILLS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input
            type="text"
            value={blockSkillReason}
            onChange={e => setBlockSkillReason(e.target.value)}
            placeholder="Reason (optional)"
            className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <button
            onClick={() => {
              if (!blockSkillInput) return;
              blockSkill(blockSkillInput, blockSkillReason);
              setBlockSkillInput('');
              setBlockSkillReason('');
            }}
            disabled={!blockSkillInput}
            className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-50 rounded-lg text-xs text-white font-semibold transition-colors"
          >
            Block
          </button>
        </div>

        {blockedSkills.length === 0 ? (
          <p className="text-xs text-slate-500">No skills currently blocked.</p>
        ) : (
          <div className="space-y-2">
            {blockedSkills.map(sk => (
              <div key={sk.skill_name} className="flex items-center justify-between bg-red-900/20 border border-red-800/40 rounded-lg px-3 py-2">
                <div>
                  <span className="text-sm font-semibold text-red-300 font-mono">{sk.skill_name}</span>
                  {sk.reason && <span className="text-xs text-slate-400 ml-2">{sk.reason}</span>}
                </div>
                <button
                  onClick={() => unblockSkill(sk.skill_name)}
                  className="flex items-center gap-1 px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs text-slate-300 transition-colors"
                >
                  <Unlock className="w-3 h-3" />
                  Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Principles & Values */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Constitutional Principles
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {principles.map(principle => (
            <div key={principle.id} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <div className="mb-3">
                <span className={`inline-block px-2 py-1 rounded text-xs font-semibold mb-2 ${
                  principle.category === 'values' ? 'bg-purple-900 text-purple-200' :
                  principle.category === 'principles' ? 'bg-indigo-900 text-indigo-200' :
                  'bg-blue-900 text-blue-200'
                }`}>
                  {principle.category.toUpperCase()}
                </span>
              </div>
              <h3 className="font-semibold text-slate-100 mb-2">{principle.title}</h3>
              <p className="text-sm text-slate-400 mb-3">{principle.description}</p>

              {/* Compliance Score */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-400">Compliance</span>
                  <span className="text-sm font-bold text-cyan-400">{principle.complianceScore}%</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      principle.complianceScore >= 80 ? 'bg-emerald-500' :
                      principle.complianceScore >= 60 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${principle.complianceScore}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Decision Authority Matrix */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5" />
          Decision Authority Matrix
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-3 px-4 font-semibold text-slate-300">Role</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-300">Authority</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-300">Decision Types</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-300">Constraints</th>
              </tr>
            </thead>
            <tbody>
              {authorityMatrix.map((rule, idx) => (
                <tr key={rule.id} className={idx % 2 === 0 ? 'bg-slate-900/30' : 'bg-slate-900/10'}>
                  <td className="py-3 px-4 font-semibold text-slate-200">{rule.role}</td>
                  <td className="py-3 px-4 text-slate-400">{rule.authority}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {rule.decisionTypes.map((type, i) => (
                        <span key={i} className="px-2 py-1 rounded bg-indigo-900/50 text-indigo-200 text-xs">
                          {type}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {rule.constraints && rule.constraints.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {rule.constraints.map((constraint, i) => (
                          <span key={i} className="px-2 py-1 rounded bg-yellow-900/50 text-yellow-200 text-xs">
                            {constraint}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Compliance Dashboard */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          Compliance Status
        </h2>
        <div className="space-y-4">
          {complianceStatuses.map(compliance => (
            <div key={compliance.standard} className="bg-slate-900/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-100">{compliance.standard}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(compliance.status)}`}>
                  {compliance.status.toUpperCase()}
                </span>
              </div>

              {/* Score */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-400">Compliance Score</span>
                  <span className="text-sm font-bold text-cyan-400">{compliance.score}%</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      compliance.score >= 80 ? 'bg-emerald-500' :
                      compliance.score >= 60 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${compliance.score}%` }}
                  />
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-xs text-slate-400">
                  <span className="block">Last Audit</span>
                  <span className="font-semibold text-slate-200">{compliance.lastAudit.toLocaleDateString()}</span>
                </div>
                <div className="text-xs text-slate-400">
                  <span className="block">Requirements</span>
                  <span className="font-semibold text-slate-200">{compliance.requirements.length} items</span>
                </div>
              </div>

              {/* Requirements Preview */}
              <div className="mt-3 pt-3 border-t border-slate-700">
                <div className="text-xs text-slate-400 mb-2">Key Requirements:</div>
                <div className="flex flex-wrap gap-2">
                  {compliance.requirements.slice(0, 3).map((req, i) => (
                    <span key={i} className="px-2 py-1 rounded bg-slate-700 text-slate-300 text-xs">
                      {req}
                    </span>
                  ))}
                  {compliance.requirements.length > 3 && (
                    <span className="px-2 py-1 rounded bg-slate-700 text-slate-300 text-xs">
                      +{compliance.requirements.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Registry */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Risk Registry (Top 10)
        </h2>
        <div className="space-y-3">
          {risks.map(risk => (
            <div key={risk.id} className={`rounded-lg p-4 border-l-4 ${
              risk.status === 'open' ? 'bg-red-900/20 border-l-red-500' :
              risk.status === 'mitigating' ? 'bg-yellow-900/20 border-l-yellow-500' :
              'bg-emerald-900/20 border-l-emerald-500'
            }`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-slate-100">{risk.title}</h3>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  risk.status === 'open' ? 'bg-red-900 text-red-200' :
                  risk.status === 'mitigating' ? 'bg-yellow-900 text-yellow-200' :
                  'bg-emerald-900 text-emerald-200'
                }`}>
                  {risk.status.toUpperCase()}
                </span>
              </div>

              <p className="text-sm text-slate-400 mb-2">{risk.description}</p>

              {/* Risk Matrix */}
              <div className="grid grid-cols-3 gap-2 mb-2 text-xs">
                <div className="bg-slate-900/50 rounded p-2">
                  <span className="text-slate-400">Likelihood</span>
                  <span className={`block font-semibold capitalize ${
                    risk.likelihood === 'high' ? 'text-red-400' :
                    risk.likelihood === 'medium' ? 'text-yellow-400' :
                    'text-emerald-400'
                  }`}>
                    {risk.likelihood}
                  </span>
                </div>
                <div className="bg-slate-900/50 rounded p-2">
                  <span className="text-slate-400">Impact</span>
                  <span className={`block font-semibold capitalize ${
                    risk.impact === 'high' ? 'text-red-400' :
                    risk.impact === 'medium' ? 'text-yellow-400' :
                    'text-emerald-400'
                  }`}>
                    {risk.impact}
                  </span>
                </div>
                <div className="bg-slate-900/50 rounded p-2">
                  <span className="text-slate-400">Risk Level</span>
                  <span className={`block font-semibold ${
                    (risk.likelihood === 'high' && risk.impact === 'high') ? 'text-red-400' :
                    (risk.likelihood === 'high' || risk.impact === 'high') ? 'text-yellow-400' :
                    'text-emerald-400'
                  }`}>
                    {(risk.likelihood === 'high' && risk.impact === 'high') ? 'Critical' :
                      (risk.likelihood === 'high' || risk.impact === 'high') ? 'High' :
                      'Medium'}
                  </span>
                </div>
              </div>

              {risk.mitigation && (
                <div className="bg-slate-900/50 rounded p-2 mt-2">
                  <span className="text-xs text-slate-400 block mb-1">Mitigation Strategy</span>
                  <span className="text-sm text-slate-300">{risk.mitigation}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Governor Decisions */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Gavel className="w-5 h-5 text-amber-400" />
            Governor Decisions
            <span className="text-xs font-normal text-slate-400 ml-1">(last 20 non-EXECUTE)</span>
          </h2>
          <div className="flex gap-1">
            {(['all', 'queue_block', 'block'] as VerdictFilter[]).map(f => (
              <button
                key={f}
                onClick={() => setVerdictFilter(f)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  verdictFilter === f
                    ? 'bg-amber-700 text-amber-100'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {f === 'all' ? 'All' : f === 'queue_block' ? 'QUEUE+BLOCK' : 'BLOCK only'}
              </button>
            ))}
          </div>
        </div>

        {filteredDecisions.length === 0 ? (
          <p className="text-slate-400 text-sm">No governor decisions recorded yet. QUEUE and BLOCK verdicts appear here.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredDecisions.map(d => (
              <div key={d.id} className={`bg-slate-900/50 rounded-lg p-3 border-l-4 ${verdictBorder(d.routing_verdict)} flex items-start gap-3`}>
                <span className={`px-2 py-0.5 rounded text-xs font-bold shrink-0 ${verdictBadge(d.routing_verdict)}`}>
                  {d.routing_verdict}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-slate-200 font-mono">{d.action_class}</span>
                    <span className="text-xs text-slate-500">·</span>
                    <span className="text-xs text-slate-400">{d.agent_id}</span>
                    {d.epic_id && (
                      <>
                        <span className="text-xs text-slate-500">·</span>
                        <span className="text-xs text-slate-400 font-mono">{d.epic_id}</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-1">{reasonSnippet(d.action_detail)}</p>
                  {d.logbook_ref && (
                    <p className="text-xs text-slate-500 mt-0.5">logbook: <span className="font-mono">{d.logbook_ref}</span></p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-sm font-bold ${
                    d.risk_score >= 0.70 ? 'text-red-400' :
                    d.risk_score >= 0.35 ? 'text-amber-400' :
                    'text-emerald-400'
                  }`}>{d.risk_score.toFixed(2)}</p>
                  <p className="text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString()}</p>
                  <p className="text-xs text-slate-600 font-mono">{d.id}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Board Decisions / Governance Precedents */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-violet-400" />
          Board Decisions &amp; Precedents
          <span className="text-xs font-normal text-slate-400 ml-1">({boardDecisions.length})</span>
        </h2>
        {boardDecisions.length === 0 ? (
          <p className="text-slate-400 text-sm">No board decisions recorded yet.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {boardDecisions.map(d => {
              const rulingColor =
                d.ruling === 'approve'      ? 'bg-emerald-900 text-emerald-200' :
                d.ruling === 'block'        ? 'bg-red-900 text-red-200' :
                d.ruling === 'escalate'     ? 'bg-amber-900 text-amber-200' :
                                              'bg-indigo-900 text-indigo-200';
              const borderColor =
                d.ruling === 'approve'      ? 'border-l-emerald-500' :
                d.ruling === 'block'        ? 'border-l-red-500' :
                d.ruling === 'escalate'     ? 'border-l-amber-500' :
                                              'border-l-indigo-500';
              let pattern: Record<string, string> = {};
              try { pattern = JSON.parse(d.context_pattern || '{}'); } catch { /* */ }
              const patternKeys = Object.entries(pattern).slice(0, 4);
              return (
                <div key={d.id} className={`bg-slate-900/50 rounded-lg p-3 border-l-4 ${borderColor} flex items-start gap-3`}>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold shrink-0 uppercase ${rulingColor}`}>
                    {d.ruling}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-200 leading-snug mb-1">{d.rationale}</p>
                    {patternKeys.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {patternKeys.map(([k, v]) => (
                          <span key={k} className="px-1.5 py-0.5 bg-slate-700 text-slate-400 rounded text-xs font-mono">
                            {k}={String(v)}
                          </span>
                        ))}
                        {Object.keys(pattern).length > 4 && (
                          <span className="text-xs text-slate-500">+{Object.keys(pattern).length - 4} more</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-slate-500">by {d.created_by}</span>
                      {d.applies_to !== 'global' && (
                        <span className="text-xs text-slate-500 font-mono">{d.applies_to}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-slate-300">{d.match_count}</p>
                    <p className="text-xs text-slate-500">matches</p>
                    <p className="text-xs text-slate-600 font-mono mt-1">{d.id}</p>
                    <button
                      onClick={() => resolveBoardDecision(d.id)}
                      className="mt-1 px-2 py-0.5 rounded text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                    >
                      Resolve
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Architecture Facts */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Database className="w-5 h-5 text-cyan-400" />
            Architecture Facts
          </h2>
          <div className="flex items-center gap-2">
            <select
              value={filterDomain}
              onChange={e => setFilterDomain(e.target.value)}
              className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-xs text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            >
              <option value="">All domains</option>
              {ARCH_DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <button
              onClick={() => setShowAddFact(true)}
              className="flex items-center gap-1 px-3 py-1 bg-cyan-600 hover:bg-cyan-700 rounded text-xs text-white font-semibold transition-colors"
            >
              <Plus className="w-3 h-3" />
              Add Fact
            </button>
          </div>
        </div>
        {filteredFacts.length === 0 ? (
          <p className="text-slate-400 text-sm">No architecture facts found. Add one to document your platform decisions.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {filteredFacts.map(fact => (
              <div key={fact.id} className="bg-slate-900/50 rounded p-3 flex items-start gap-3">
                <span className="px-2 py-0.5 bg-indigo-900/60 text-indigo-300 rounded text-xs font-mono shrink-0">{fact.domain}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-200 font-mono">{fact.key}</p>
                  <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{fact.value}</p>
                </div>
                <span className={`text-xs shrink-0 ${
                  fact.confidence === 'grounded' ? 'text-emerald-400' :
                  fact.confidence === 'speculative' ? 'text-yellow-400' :
                  'text-slate-400'
                }`}>{fact.confidence}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ADR History */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          Architecture Decision Record (ADR) History
        </h2>
        <div className="space-y-4">
          {adrHistory.map((adr, idx) => (
            <div key={adr.id} className="relative">
              {idx < adrHistory.length - 1 && (
                <div className="absolute left-6 top-16 h-6 w-0.5 bg-slate-700" />
              )}

              <div className="flex gap-4">
                {/* Timeline Dot */}
                <div className="flex flex-col items-center pt-1">
                  <div className={`w-3 h-3 rounded-full ${
                    adr.status === 'accepted' ? 'bg-emerald-500' :
                    adr.status === 'deprecated' ? 'bg-red-500' :
                    'bg-yellow-500'
                  }`} />
                </div>

                {/* Content */}
                <div className="flex-1 bg-slate-900/50 rounded-lg p-4 pb-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-slate-100">{adr.title}</h3>
                      <p className="text-xs text-slate-400 mt-1">
                        {adr.date.toLocaleDateString()} • {adr.author}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusColor(adr.status)}`}>
                      {adr.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="space-y-3 mt-3 text-sm">
                    <div>
                      <span className="text-slate-400">Context:</span>
                      <p className="text-slate-300 mt-1">{adr.context}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Decision:</span>
                      <p className="text-slate-300 mt-1">{adr.decision}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Consequences:</span>
                      <p className="text-slate-300 mt-1">{adr.consequences}</p>
                    </div>
                    {adr.alternatives && adr.alternatives.length > 0 && (
                      <div>
                        <span className="text-slate-400">Alternatives Considered:</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {adr.alternatives.map((alt, i) => (
                            <span key={i} className="px-2 py-1 rounded bg-slate-700 text-slate-300 text-xs">
                              {alt}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Governance Score Card */}
      <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border border-purple-700/50 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-purple-200 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Overall Governance Score
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Principles Compliance', value: 88 },
            { label: 'Authority Clarity', value: 95 },
            { label: 'Compliance Readiness', value: 78 },
            { label: 'Risk Management', value: 82 },
          ].map((metric, idx) => (
            <div key={idx} className="bg-slate-900/70 rounded-lg p-4">
              <p className="text-xs text-slate-400 mb-2">{metric.label}</p>
              <div className="mb-2">
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      metric.value >= 80 ? 'bg-emerald-500' :
                      metric.value >= 60 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${metric.value}%` }}
                  />
                </div>
              </div>
              <p className="text-lg font-bold text-purple-300">{metric.value}%</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
